<template>
  <section
    v-if="category.items && category.items.length > 0"
    :id="categorySectionId"
    class="py-6 md:py-8 scroll-mt-48"
  >
    <div class="mb-6">
      <h2 class="text-2xl font-serif font-bold text-gray-900 mb-2">
        {{ categoryName }}
      </h2>
      <p v-if="category.description" class="text-gray-500 text-sm md:text-base">
        {{ category.description }}
      </p>
    </div>
    <!-- List layout: mobile search results (app card style, image + button on the right) -->
    <div v-if="listMode" class="md:hidden flex flex-col gap-4">
      <div
        v-for="item in category.items"
        :key="`list-${item.id}`"
        @click="handleItemClick(item)"
        class="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex gap-4"
        :class="
          isUnavailable(item)
            ? 'opacity-60 cursor-not-allowed'
            : 'cursor-pointer'
        "
      >
        <!-- Left: text -->
        <div class="flex-1 min-w-0 flex flex-col">
          <h3 class="font-bold text-base text-gray-900 mb-1">
            {{ item.name }}
          </h3>
          <div
            v-if="item.badges && item.badges.length"
            class="flex gap-1.5 flex-wrap mb-2"
          >
            <span
              v-for="(badge, idx) in item.badges"
              :key="idx"
              class="text-xs font-bold px-2.5 py-0.5 rounded-full"
              :style="{
                backgroundColor: badge.bg_color || '#14c240',
                color: badge.text_color || '#FFFFFF'
              }"
            >
              {{ badge.text }}
            </span>
          </div>
          <p
            v-if="item.description"
            class="text-gray-500 text-sm leading-snug mb-2"
          >
            {{ truncateDescription(item.description) }}
          </p>
          <div
            class="mt-auto flex items-center gap-2 text-sm text-gray-500 flex-wrap"
          >
            <span class="font-medium text-gray-900">
              {{ formatPrice(item.price) }}
            </span>
            <span v-if="item.calories">• {{ item.calories }} cal</span>
            <span
              v-if="item.spicy"
              class="flex items-center gap-1 text-red-500"
            >
              • <Flame :size="12" style="fill: currentColor;" /> Spicy
            </span>
            <span
              v-if="item.vegan"
              class="flex items-center gap-1 text-green-600"
            >
              • <Leaf :size="12" style="fill: currentColor;" /> Vegan
            </span>
          </div>
        </div>
        <!-- Right: image with the action button directly underneath -->
        <div class="flex-none w-28 flex flex-col gap-2">
          <div class="relative h-24 w-full overflow-hidden rounded-xl bg-white">
            <img
              v-if="getItemImage(item)"
              :src="getItemImage(item)"
              :alt="item.name"
              class="w-full h-full object-contain"
              @error="handleImageError"
            />
            <div
              v-else
              class="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200"
            >
              <FileImage :size="28" class="text-gray-400" />
            </div>
          </div>
          <button
            v-if="isBuyNowEnabled"
            type="button"
            @click.stop="handleBuyNowClick(item)"
            :disabled="isUnavailable(item)"
            class="w-full text-xs font-semibold py-2 px-2 rounded-lg shadow-sm transition-all"
            :class="
              isUnavailable(item)
                ? 'bg-gray-300 text-gray-600 cursor-not-allowed shadow-none'
                : 'bg-primary-600 hover:bg-primary-700 text-white active:scale-95'
            "
          >
            {{
              isUnavailable(item) ? "Unavailable" : getBuyNowButtonLabel(item)
            }}
          </button>
          <button
            v-else-if="!isUnavailable(item)"
            type="button"
            @click.stop="handleItemClick(item)"
            class="self-center bg-white text-gray-900 border border-gray-200 p-2 rounded-full shadow-sm hover:bg-primary-50 hover:text-primary-600 transition-all"
          >
            <Plus :size="18" />
          </button>
          <span
            v-else
            class="self-center bg-gray-900 text-white text-[10px] font-bold px-2 py-1 rounded-full"
          >
            Unavailable
          </span>
        </div>
      </div>
    </div>

    <!-- Grid layout: default browsing + all desktop views -->
    <div
      class="grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
      :class="listMode ? 'hidden md:grid' : 'grid'"
    >
      <div
        v-for="item in category.items"
        :key="item.id"
        @click="handleItemClick(item)"
        class="group bg-white rounded-2xl border border-gray-100 shadow-sm transition-all duration-300 overflow-hidden flex flex-col"
        :class="
          isUnavailable(item)
            ? 'opacity-60 cursor-not-allowed'
            : 'cursor-pointer'
        "
      >
        <div class="relative h-56 overflow-hidden bg-white">
          <img
            v-if="getItemImage(item)"
            :src="getItemImage(item)"
            :alt="item.name"
            class="w-full h-full object-contain transition-transform duration-500 menu-item-image"
            @error="handleImageError"
          />
          <div
            v-else
            class="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200"
          >
            <FileImage :size="48" class="text-gray-400" />
          </div>
          <div
            v-if="item.badges && item.badges.length"
            class="absolute bottom-3 left-3 flex gap-2 flex-wrap max-w-[70%]"
          >
            <span
              v-for="(badge, idx) in item.badges"
              :key="idx"
              class="text-xs font-bold px-3 py-1 rounded-full shadow-lg"
              :style="{
                backgroundColor: badge.bg_color || '#14c240',
                color: badge.text_color || '#FFFFFF'
              }"
            >
              {{ badge.text }}
            </span>
          </div>
          <button
            v-if="!isBuyNowEnabled && !isUnavailable(item)"
            class="absolute flex bottom-3 right-3 bg-white text-black p-2 rounded-full shadow-lg transition-all duration-300 hover:bg-primary-50 hover:text-primary-600 menu-item-button"
          >
            <Plus :size="20" />
          </button>
          <span
            v-if="isUnavailable(item)"
            class="absolute bottom-3 right-3 bg-gray-900 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg"
          >
            Unavailable
          </span>
        </div>

        <div class="p-5 flex-1 flex flex-col">
          <div class="flex justify-between items-start mb-2">
            <h3
              class="font-bold text-lg text-gray-900 transition-colors menu-item-title"
            >
              {{ item.name }}
            </h3>
            <span class="font-medium text-gray-900">
              {{ formatPrice(item.price) }}
            </span>
          </div>

          <p
            v-if="item.description"
            class="text-gray-500 text-sm leading-relaxed mb-4 flex-1"
          >
            {{ truncateDescription(item.description) }}
            <button
              v-if="isDescriptionTruncated(item.description)"
              type="button"
              @click.stop="handleItemClick(item)"
              class="inline-flex items-center text-xs font-bold text-primary-600 hover:text-primary-700 hover:underline ml-0.5"
            >
              Read more
            </button>
          </p>

          <button
            v-if="isBuyNowEnabled"
            @click.stop="handleBuyNowClick(item)"
            class="w-full mb-4 font-semibold py-3 px-4 rounded-xl shadow-md transition-all duration-300"
            :class="
              isUnavailable(item)
                ? 'bg-gray-300 text-gray-600 cursor-not-allowed shadow-none'
                : 'bg-primary-600 hover:bg-primary-700 text-white active:scale-95'
            "
            :disabled="isUnavailable(item)"
          >
            {{
              isUnavailable(item) ? "Unavailable" : getBuyNowButtonLabel(item)
            }}
          </button>

          <div class="flex items-center gap-3 text-xs text-gray-500 mt-auto">
            <span v-if="item.calories" class="bg-gray-50 px-2 py-1 rounded"
              >{{ item.calories }} cal</span
            >
            <span
              v-if="item.spicy"
              class="flex items-center gap-1 text-red-500 bg-red-50 px-2 py-1 rounded"
            >
              <Flame :size="12" style="fill: currentColor;" />
              Spicy
            </span>
            <span
              v-if="item.vegan"
              class="flex items-center gap-1 text-green-600 bg-green-50 px-2 py-1 rounded"
            >
              <Leaf :size="12" style="fill: currentColor;" />
              Vegan
            </span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "MenuSection",
  props: {
    category: {
      type: Object,
      required: true
    },
    isBuyNowEnabled: {
      type: Boolean,
      default: false
    },
    /** Render the compact card-row layout on mobile (used for search results). */
    listMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      descriptionMaxLength: 100
    };
  },
  computed: {
    ...mapGetters(["formatPrice"]),
    categorySectionId() {
      // Use format cat_${id} for backend categories, or category.id if already formatted
      if (
        typeof this.category.id === "string" &&
        this.category.id.startsWith("cat_")
      ) {
        return this.category.id;
      }
      return `cat_${this.category.id}`;
    },
    categoryName() {
      // Backend uses 'name', fallback uses 'title'
      return this.category.name || this.category.title || "";
    }
  },
  methods: {
    isUnavailable(item) {
      const isProduct86d = this.$store.getters["store/isProduct86d"];
      return !!(item && isProduct86d && isProduct86d(item.id));
    },
    handleItemClick(item) {
      if (this.isUnavailable(item)) return;
      this.$emit("item-click", item);
    },
    isDescriptionTruncated(description) {
      return !!description && description.length > this.descriptionMaxLength;
    },
    truncateDescription(description) {
      if (!description) return "";
      if (description.length <= this.descriptionMaxLength) return description;
      return `${description.slice(0, this.descriptionMaxLength).trimEnd()}...`;
    },
    handleBuyNowClick(item) {
      if (this.isUnavailable(item)) return;
      this.$emit("buy-now-click", item);
    },
    getItemImage(item) {
      return item?.image || item?.image_url || null;
    },
    getBuyNowButtonLabel(item) {
      return item?.has_modifier_groups === true ||
        item?.hasModifierGroups === true
        ? "Customize"
        : "Add to Cart";
    },
    handleImageError(event) {
      // Hide broken image and show placeholder
      event.target.style.display = "none";
    }
  }
};
</script>

<style scoped>
/* Group hover effects */
.group:hover {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.group:hover .menu-item-image {
  transform: scale(1.05);
}

.group:hover .menu-item-button {
  opacity: 1;
  transform: translateY(0);
}

.group:hover .menu-item-title {
  color: rgb(29, 78, 216);
}

.menu-item-button {
  opacity: 0;
  transform: translateY(8px);
}
</style>
